/**
 * Custom hooks per Document Attributes
 */
import { UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiDeleteWithBody, apiGet, apiPost, apiPut } from "@/lib/api-client";
import type { DocumentAttributeCreate, DocumentAttributeListFilters, DocumentAttributeResponse, DocumentAttributeUpdate, AttributeUsageDetailResponse } from "@/types/api/document-schemas";

const DOCUMENT_ATTRIBUTES_QUERY_KEYS = {
    all: ["document-attributes"] as const,
    lists: () => [...DOCUMENT_ATTRIBUTES_QUERY_KEYS.all, "list"] as const,
    list: (filters?: DocumentAttributeListFilters) => [...DOCUMENT_ATTRIBUTES_QUERY_KEYS.lists(), filters] as const,
    details: () => [...DOCUMENT_ATTRIBUTES_QUERY_KEYS.all, "detail"] as const,
    detail: (id: string) => [...DOCUMENT_ATTRIBUTES_QUERY_KEYS.details(), id] as const,
    usage: (id: string) => [...DOCUMENT_ATTRIBUTES_QUERY_KEYS.all, "usage", id] as const,
};

/**
 * Hook per lista document attributes con filtri
 */
export function useDocumentAttributes(
    filters?: DocumentAttributeListFilters,
    options?: Omit<UseQueryOptions<DocumentAttributeResponse[], Error>, "queryKey" | "queryFn">,
) {
    return useQuery({
        queryKey: DOCUMENT_ATTRIBUTES_QUERY_KEYS.list(filters),
        queryFn: async (): Promise<DocumentAttributeResponse[]> => {
            return await apiGet<DocumentAttributeResponse[]>("/document-attributes", {
                params: filters as Record<string, string | number | boolean | null | undefined> | undefined,
            });
        },
        ...options,
    });
}

/**
 * Hook per singolo document attribute
 */
export function useDocumentAttribute(attributeId: string) {
    return useQuery({
        queryKey: DOCUMENT_ATTRIBUTES_QUERY_KEYS.detail(attributeId),
        queryFn: async (): Promise<DocumentAttributeResponse> => {
            return await apiGet<DocumentAttributeResponse>(`/document-attributes/${attributeId}`);
        },
        enabled: !!attributeId,
    });
}

/**
 * Hook per dettaglio usage di un attributo (in quali schemas è usato)
 */
export function useAttributeUsageDetail(attributeId: string | null) {
    return useQuery({
        queryKey: DOCUMENT_ATTRIBUTES_QUERY_KEYS.usage(attributeId || ''),
        queryFn: async (): Promise<AttributeUsageDetailResponse> => {
            return await apiGet<AttributeUsageDetailResponse>(
                `/document-attributes/${attributeId}/schemas`
            );
        },
        enabled: !!attributeId,
    });
}

/**
 * Hook per creare document attribute
 */
export function useCreateDocumentAttribute() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: DocumentAttributeCreate): Promise<DocumentAttributeResponse> => {
            return await apiPost<DocumentAttributeResponse>("/document-attributes", data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: DOCUMENT_ATTRIBUTES_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per aggiornare document attribute
 */
export function useUpdateDocumentAttribute() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ attributeId, data }: { attributeId: string; data: DocumentAttributeUpdate }): Promise<DocumentAttributeResponse> => {
            return await apiPut<DocumentAttributeResponse>(`/document-attributes/${attributeId}`, data);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: DOCUMENT_ATTRIBUTES_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: DOCUMENT_ATTRIBUTES_QUERY_KEYS.detail(variables.attributeId) });
        },
    });
}

/**
 * Hook per eliminare document attribute
 */
export function useDeleteDocumentAttribute() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (attributeId: string): Promise<void> => {
            return await apiDelete<void>(`/document-attributes/${attributeId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: DOCUMENT_ATTRIBUTES_QUERY_KEYS.lists() });
        },
    });
}

export interface AttributeSchemaUsage {
    document_attribute_id: string;
    attribute_label: string;
    schema_names: string[];
}

export interface BatchDeleteAttributesResponse {
    deleted_count: number;
    failed_count: number;
    attributes_in_use: AttributeSchemaUsage[];
}

/**
 * Hook per eliminare document attributes in batch
 */
export function useDeleteDocumentAttributesBatch() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ attributeIds, force }: { attributeIds: string[]; force: boolean }): Promise<BatchDeleteAttributesResponse> => {
            return await apiDeleteWithBody<BatchDeleteAttributesResponse>(
                "/document-attributes/batch",
                { attribute_ids: attributeIds, force }
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: DOCUMENT_ATTRIBUTES_QUERY_KEYS.lists() });
        },
    });
}
